import { useCallback, useEffect, useState } from 'react';
import { fetchProductCatalog } from '../api/products';
import type { ComingSoonProduct, ProductCatalogEntry } from '../types/api';

const PRODUCT_STORAGE_KEY = 'mikeai:selectedProduct';

function readStoredSlug(): string | null {
  try {
    return localStorage.getItem(PRODUCT_STORAGE_KEY);
  } catch {
    return null;
  }
}

export interface UseProductCatalogResult {
  products: ProductCatalogEntry[];
  comingSoon: ComingSoonProduct[];
  loading: boolean;
  error: boolean;
  selectedSlug: string | null;
  /** The docs version a new conversation will use; the product's newest unless changed. */
  selectedVersion: string | null;
  /** Select a product and one of its versions; no version means its newest. */
  selectProduct: (slug: string, version?: string) => void;
}

// Versions arrive newest first, and the newest is the default.
function newestVersion(products: ProductCatalogEntry[], slug: string | null): string | null {
  return products.find((p) => p.slug === slug)?.versions[0] ?? null;
}

export function useProductCatalog(): UseProductCatalogResult {
  const [products, setProducts] = useState<ProductCatalogEntry[]>([]);
  const [comingSoon, setComingSoon] = useState<ComingSoonProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Retry once, silently, before giving up and exposing the error flag.
    const load = async () => {
      try {
        return await fetchProductCatalog();
      } catch {
        return await fetchProductCatalog();
      }
    };

    load()
      .then((catalog) => {
        if (cancelled) return;
        setProducts(catalog.products);
        setComingSoon(catalog.coming_soon);
        const stored = readStoredSlug();
        const isValid = catalog.products.some((p) => p.slug === stored);
        const slug = isValid ? stored : (catalog.products[0]?.slug ?? null);
        setSelectedSlug(slug);
        setSelectedVersion(newestVersion(catalog.products, slug));
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const selectProduct = useCallback(
    (slug: string, version?: string) => {
      const product = products.find((p) => p.slug === slug);
      if (!product) return;
      setSelectedSlug(slug);
      setSelectedVersion(
        version && product.versions.includes(version) ? version : newestVersion(products, slug),
      );
      try {
        localStorage.setItem(PRODUCT_STORAGE_KEY, slug);
      } catch {
        /* localStorage unavailable (e.g. private browsing) — selection still works this session */
      }
    },
    [products],
  );

  return { products, comingSoon, loading, error, selectedSlug, selectedVersion, selectProduct };
}
